/* Kiosk meters: one rAF loop drives the VU needles and the spectrum columns from a level source, writing the
   needles' transforms and toggling lit segments only where the count changed.
   Sources:
   - webAudioLevels: the browser's own AnalyserNodes on the media element (Firefox, Chrome).
   - envelopeLevels: per-track envelopes precomputed at ingest (GET /zone/levels/<track>), placed at the audible
     position. For Safari, whose Web Audio receives only zeros from an MSE-fed element and which plays HLS natively.
     They show the audio as mastered; the volume knob does not move them. Sync depends on the stream's program date
     time (the audible moment), so the player's clock is smoothed between Safari's coarse currentTime updates. */
import type { NowItem } from '../api';

const VU_ATTACK_MS = 60;
const VU_RELEASE_MS = 250;
const BAND_FALL_MS = 45; // one segment per step while a column falls

/** One reading: dBFS per channel and spectrum bands as analyser bytes (0..255). null = nothing audible. */
export type Levels = { db: [number, number]; bands: ArrayLike<number> } | null;
export type LevelSource = { read(): Levels; ctx?: AudioContext; stop?(): void };

export type MeterTargets = {
  needles: SVGLineElement[]; // [L, R]
  vuSvgs?: SVGSVGElement[];
  columns: HTMLDivElement[];
};

/** Dial scale (dBFS -> degrees from vertical), shared with the VuMeter face so the printed scale is the real one.
 *  Spread like a VU face: wide in the -20..0 range where music lives. */
export const VU_SCALE: [number, number][] = [[-40, -45], [-20, -20], [-10, 0], [-5, 15], [0, 32], [3, 45]];

export function vuAngle(db: number): number {
  const d = Math.max(-40, Math.min(3, db));
  for (let i = 1; i < VU_SCALE.length; i++) {
    const [d0, a0] = VU_SCALE[i - 1];
    const [d1, a1] = VU_SCALE[i];
    if (d <= d1) return a0 + ((d - d0) / (d1 - d0)) * (a1 - a0);
  }
  return 45;
}

/** Log-spaced band edges, 60 Hz .. 16 kHz (the backend's audio.level_envelope uses the same ones). */
export const bandEdges = (bands: number) => Array.from({ length: bands + 1 }, (_, i) => 60 * Math.pow(16000 / 60, i / bands));

export function webAudioLevels(audio: HTMLAudioElement, bands: number): LevelSource {
  const Ctx = window.AudioContext || (window as any).webkitAudioContext;
  const ctx: AudioContext = new Ctx();
  const src = ctx.createMediaElementSource(audio);
  const split = ctx.createChannelSplitter(2);
  const left = ctx.createAnalyser();
  const right = ctx.createAnalyser();
  const spec = ctx.createAnalyser();
  for (const a of [left, right, spec]) a.fftSize = 2048;
  spec.smoothingTimeConstant = 0.3;
  src.connect(ctx.destination);
  src.connect(split);
  src.connect(spec);
  split.connect(left, 0);
  split.connect(right, 1);

  const binHz = ctx.sampleRate / spec.fftSize;
  const edges = bandEdges(bands);
  const bandBins = edges.slice(0, -1).map((lo, i) => [Math.max(1, Math.floor(lo / binHz)), Math.max(2, Math.ceil(edges[i + 1] / binHz))]);
  const td = new Float32Array(left.fftSize);
  const fd = new Uint8Array(spec.frequencyBinCount);
  const out = new Array(bands).fill(0);

  function rms(a: AnalyserNode) {
    a.getFloatTimeDomainData(td);
    let s = 0;
    for (let i = 0; i < td.length; i++) s += td[i] * td[i];
    return 20 * Math.log10(Math.sqrt(s / td.length) + 1e-9);
  }

  return {
    ctx,
    read() {
      spec.getByteFrequencyData(fd);
      bandBins.forEach(([lo, hi], b) => {
        let m = 0;
        for (let i = lo; i < hi && i < fd.length; i++) m = Math.max(m, fd[i]);
        out[b] = m;
      });
      return { db: [rms(left), rms(right)], bands: out };
    },
    stop() { void ctx.close(); },
  };
}

type Envelope = { fps: number; bands: number; frames: Uint8Array };
/** Something on air with an envelope: a song (track id) or a voiced DJ link; key null = nothing to show. */
export type EnvelopeItem = { key: string | null; started_at: string; duration_ms: number } | null;

export function envelopeLevels(opts: { url: (key: string) => string; audibleMs: () => number | null; items: () => EnvelopeItem[];
  bands: number }): LevelSource {
  const cache = new Map<string, Envelope | null | 'loading'>();
  const out = new Array(opts.bands).fill(0);

  function load(key: string) {
    if (cache.has(key)) return;
    cache.set(key, 'loading');
    fetch(opts.url(key))
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(r.status)))
      .then((buf) => {
        const b = new Uint8Array(buf);
        if (String.fromCharCode(...b.subarray(0, 4)) !== 'SFL1') throw new Error('bad envelope');
        cache.set(key, { fps: b[4], bands: b[5], frames: b.subarray(8) });
      })
      .catch((e) => {
        cache.set(key, null);
        if (e !== 404) setTimeout(() => cache.delete(key), 30_000); // network trouble: try again later
      });
    if (cache.size > 8) cache.delete(cache.keys().next().value!); // the oldest: items long gone
  }

  return {
    read() {
      const items = opts.items();
      for (const it of items) if (it?.key) load(it.key); // what comes next loads before it starts
      const t = opts.audibleMs();
      if (t === null) return null;
      const it = items.find((i) => i?.key && t >= Date.parse(i.started_at) && t < Date.parse(i.started_at) + i.duration_ms);
      const env = it?.key ? cache.get(it.key) : null;
      if (!it || !env || env === 'loading') return null;
      const w = 2 + env.bands;
      const n = env.frames.length / w;
      // Frame i is the analyser window that ends at (i + 1) / fps: at time t the latest complete one is t * fps - 1.
      const pos = Math.max(0, ((t - Date.parse(it.started_at)) / 1000) * env.fps - 1);
      const i = Math.min(n - 1, Math.floor(pos));
      const j = Math.min(n - 1, i + 1);
      const k = pos - i;
      // The needles glide between frames (their ballistics smooth them anyway). The spectrum steps to the latest
      // frame, as an analyser would: gliding columns read as sluggish, and a beat must land when it is heard.
      const lv = (c: number) => env.frames[i * w + c] * (1 - k) + env.frames[j * w + c] * k;
      // The envelope's bands spread over the meter's (same 60 Hz..16 kHz log scale, maybe a different count).
      for (let b = 0; b < opts.bands; b++) {
        const x = Math.max(0, Math.min(env.bands - 1, ((b + 0.5) / opts.bands) * env.bands - 0.5));
        const x0 = Math.floor(x);
        const x1 = Math.min(env.bands - 1, x0 + 1);
        out[b] = env.frames[i * w + 2 + x0] * (1 - (x - x0)) + env.frames[i * w + 2 + x1] * (x - x0);
      }
      return { db: [lv(0) / 4 - 60, lv(1) / 4 - 60], bands: out };
    },
  };
}

export function startMeters(source: LevelSource, targets: () => MeterTargets, bands: number) {
  const vu = [-40, -40];
  const lit = new Array(bands).fill(0);
  const shown = new Array(bands).fill(-1);
  const stepAt = new Array(bands).fill(0);
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let last = performance.now();
  let lastAria = 0;
  let raf = 0;

  function frame(now: number) {
    raf = requestAnimationFrame(frame);
    const dt = now - last;
    last = now;
    const t = targets();
    const lv = source.read();
    [0, 1].forEach((ch) => {
      const db = lv ? lv.db[ch] : -60;
      const tau = db > vu[ch] ? VU_ATTACK_MS : VU_RELEASE_MS;
      vu[ch] += (db - vu[ch]) * (1 - Math.exp(-dt / tau));
      const n = t.needles[ch];
      if (n) n.style.transform = `rotate(${vuAngle(vu[ch]).toFixed(2)}deg)`;
    });
    if (now - lastAria > 1000 && t.vuSvgs) {
      lastAria = now;
      t.vuSvgs.forEach((svg, ch) => svg?.setAttribute('aria-label',
        `${ch ? 'Right' : 'Left'} channel minus ${Math.round(Math.abs(Math.min(0, vu[ch])))} decibels`));
    }

    for (let b = 0; b < bands; b++) {
      const target = lv ? Math.round((lv.bands[b] / 255) * 12) : 0;
      // Attack at once, release a segment at a time: columns fall, they do not flicker.
      if (target >= lit[b] || reduce) { lit[b] = target; stepAt[b] = now; }
      else if (now - stepAt[b] > BAND_FALL_MS) { lit[b] = Math.max(target, lit[b] - 1); stepAt[b] = now; }
      const col = t.columns[b];
      if (col && shown[b] !== lit[b]) {
        const segs = col.children;
        for (let i = 0; i < segs.length; i++) segs[i].classList.toggle('on', i < lit[b]);
        shown[b] = lit[b];
      }
    }
  }

  const onVis = () => {
    if (document.hidden) cancelAnimationFrame(raf); // meters freeze, audio continues
    else { last = performance.now(); raf = requestAnimationFrame(frame); }
  };
  document.addEventListener('visibilitychange', onVis);
  raf = requestAnimationFrame(frame);

  return {
    ctx: source.ctx,
    resume: () => source.ctx?.resume(),
    stop() { cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', onVis); source.stop?.(); },
  };
}
